import React, { useState } from 'react'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { useTranslation } from 'react-i18next'

import { Dialog, snackbar, ToggleSwitch } from 'components'
import { useApi, useApiCall } from 'hooks'
import { ERRORS } from 'consts'

import { getMemberships } from '../../Memberships/api'
import { Membership, GetMembershipsResponse } from '../../Memberships/types'
import { getActiveTags } from '../../Tags/api'
import { Tag, GetTagsResponse } from '../../Tags/types'
import { getActiveStores } from '../../Stores/api'
import { Store, GetStoresResponse } from '../../Stores/types'
import { updateParkingConfig } from '../api'
import {
  ParkingConfig,
  ParkingConfigFormPayload,
  ParkingConfigResponse,
  UpdateParkingConfigData,
} from '../types'
import { getParkingConfigErrorToast } from '../errorMessage'
import { ParkingConfigForm } from './ParkingConfigForm'
import { useStyles } from './styles'

interface UpdateParkingConfigDialogProps {
  handleClose: () => void
  onDone: (parkingConfig: ParkingConfig) => void
  parkingConfig: ParkingConfig
}

const getMembershipsResponseGetter = (responseData: GetMembershipsResponse) =>
  responseData?.memberships ?? []
const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []
const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []

const UpdateParkingConfigDialog: React.FC<UpdateParkingConfigDialogProps> = ({
  handleClose,
  onDone,
  parkingConfig,
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [active, setActive] = useState(parkingConfig.active ?? false)
  const [{ data: memberships }] = useApi<GetMembershipsResponse, Membership[]>(
    getMemberships,
    getMembershipsResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )
  const tagIncludeIds = parkingConfig.tags?.map(tag => tag.id) ?? []
  const storeIncludeIds = parkingConfig.stores?.map(store => store.id) ?? []

  const [{ data: tags }] = useApi<GetTagsResponse, Tag[]>(
    () => getActiveTags({ group: 'BRANDS', includeIds: tagIncludeIds }),
    getTagsResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
      deps: [tagIncludeIds.join(',')],
    },
  )
  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    () => getActiveStores({ includeIds: storeIncludeIds }),
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
      deps: [storeIncludeIds.join(',')],
    },
  )

  const [updateParkingConfigApi, isLoading] = useApiCall<
    UpdateParkingConfigData,
    ParkingConfigResponse
  >(updateParkingConfig)

  const handleSubmit = async (payload: ParkingConfigFormPayload) => {
    try {
      const { parkingConfig: updatedParkingConfig } = await updateParkingConfigApi({
        id: parkingConfig.id,
        payload,
      })
      onDone(updatedParkingConfig)
    } catch (err) {
      console.error(err)
      const { message, variant } = getParkingConfigErrorToast(err, 'parking:errors.updateFailed')
      snackbar.show(message, variant)
    }
  }

  return (
    <Dialog
      title={t('parking:editTitle') as string}
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText={t('parking:save') as string}
      okButtonProps={{ disabled: isLoading }}
      style={{ width: 720, maxWidth: 'calc(100vw - 32px)' }}
      contentStyle={{ width: '100%', boxSizing: 'border-box', paddingTop: 8 }}
      overflow
      actionsAccessory={
        <FormControlLabel
          className={classes.actionsActiveToggle}
          labelPlacement="start"
          label={t('parking:fields.active') as string}
          control={
            <ToggleSwitch
              checked={active}
              onChange={event => setActive(event.target.checked)}
              disabled={isLoading}
            />
          }
        />
      }
      onAccept={() => {
        const form = document.getElementById('update-parking-config-form') as HTMLFormElement | null
        form?.requestSubmit()
      }}
    >
      <ParkingConfigForm
        formId="update-parking-config-form"
        initialConfig={parkingConfig}
        isLoading={isLoading}
        memberships={memberships}
        tags={tags}
        stores={stores}
        active={active}
        onSubmit={handleSubmit}
      />
    </Dialog>
  )
}

export { UpdateParkingConfigDialog }
